import { useRouter } from 'expo-router';
import {
  CloudRain,
  CloudSnow,
  CloudSun,
  Droplets,
  Snowflake,
  Sun,
  Sunrise as SunriseIcon,
  Sunset as SunsetIcon,
  Thermometer,
  Wind,
} from 'lucide-react-native';
import { ComponentType, useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';

/**
 * Current weather + frost warning, via Open-Meteo (free, no API key, no
 * User-Agent quirks — unlike NOAA, which silently 403'd RN-Android fetches
 * because it strips the User-Agent header).
 *
 * Geocoding strategy:
 *  - Prefer `lat`/`lon` passed in (set by Places autocomplete during
 *    onboarding) — zero geocode call needed.
 *  - Fall back to Open-Meteo's free geocoding API for the ZIP, which works
 *    cleanly on Android (no User-Agent header required).
 *
 * Result is cached in module-level memory for the session so we don't hit
 * the API on every Home-tab focus. Key changes invalidate the cache.
 *
 * On error (non-US ZIPs, network down) the panel quietly hides itself —
 * weather is a nicety, not a load-bearing feature.
 */

interface DayForecast {
  name: string; // "Tomorrow", "Thu", etc.
  highF: number | null;
  lowF: number | null;
  shortForecast: string;
  iconKey: string;
}

interface WeatherSnapshot {
  tempF: number;
  feelsLikeF: number | null;
  shortForecast: string;
  iconKey: string;
  windSpeed: string | null;
  humidityPct: number | null;
  uvIndexToday: number | null;
  /** Local ISO time strings for today's sunrise/sunset; render-side
   *  formats to a short clock string. */
  sunrise: string | null;
  sunset: string | null;
  /** Lowest forecast temp in the next 48h, for frost detection. */
  next48LowF: number | null;
  /** Up to 7 upcoming days; the panel renderer slices by viewport. */
  upcoming: DayForecast[];
  cachedAt: number;
}

const CACHE = new Map<string, WeatherSnapshot>();
const CACHE_TTL_MS = 30 * 60 * 1000;

export function WeatherPanel({
  zip,
  lat,
  lon,
  compact = false,
}: {
  zip: string | null;
  lat?: number | null;
  lon?: number | null;
  /** Wave 71: rail-sized layout for the 280px desktop sidebar — the
   *  full panel's multi-column grid collapsed into soup there. */
  compact?: boolean;
}) {
  const router = useRouter();
  const { width: viewportWidth } = useWindowDimensions();
  const [data, setData] = useState<WeatherSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [hidden, setHidden] = useState(false);
  // Forecast days to show based on viewport — phones get a glance, tablets
  // get the work week, desktops get a full 7-day. We fetch all 7 every
  // time (Open-Meteo charges the same per call), so this is pure UI.
  const forecastDays = viewportWidth >= 1024 ? 7 : viewportWidth >= 700 ? 5 : 2;

  // Cache key prefers explicit coords; falls back to ZIP. Empty means we
  // have nothing to look up, hide the panel.
  const cacheKey =
    lat != null && lon != null
      ? `c:${lat.toFixed(3)},${lon.toFixed(3)}`
      : zip && /^\d{5}$/.test(zip)
      ? `z:${zip}`
      : '';

  const [fetchError, setFetchError] = useState<string | null>(null);
  useEffect(() => {
    if (!cacheKey) {
      // No usable location — keep the panel visible as a tap-to-add prompt
      // instead of disappearing silently. On web especially, the panel
      // vanishing makes users think weather is "broken" when really we
      // just don't have a ZIP yet (fresh browser OPFS = fresh profile).
      setHidden(false);
      setFetchError(null);
      return;
    }
    setHidden(false);
    setFetchError(null);

    const cached = CACHE.get(cacheKey);
    if (cached && Date.now() - cached.cachedAt < CACHE_TTL_MS) {
      setData(cached);
      return;
    }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const snap = await fetchWeather({ lat, lon, zip });
        if (cancelled) return;
        CACHE.set(cacheKey, snap);
        setData(snap);
      } catch (err) {
        console.warn('weather fetch failed', err);
        if (!cancelled) {
          setFetchError(err instanceof Error ? err.message : String(err));
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cacheKey, lat, lon, zip]);

  if (hidden) return null;

  // No location → friendly tap-to-add prompt routing into the Tune
  // walkthrough so the user types a ZIP and continues straight into
  // their plan, rather than landing on the full Edit form.
  if (!cacheKey) {
    return (
      <View style={styles.wrap}>
        <Pressable
          onPress={() => router.push('/profile-setup' as never)}
          style={({ pressed }) => [styles.panel, styles.promptPanel, pressed && { opacity: 0.9 }]}>
          <Text style={styles.promptTitle}>Add your ZIP for weather</Text>
          <Text style={styles.promptSub}>
            Tap to add it in the Tune walkthrough — then today + 2-day forecast
            and a frost watch will live here.
          </Text>
        </Pressable>
      </View>
    );
  }

  // Fetch error → small inline tile so the user knows it didn't work
  // (vs the previous silent-hide that looked like a bug). The actual
  // error string is included so any remaining problem is diagnosable
  // without opening devtools — CORS, HTTP 4xx, ZIP miss all look
  // distinct here.
  if (fetchError && !data) {
    return (
      <View style={styles.wrap}>
        <View style={[styles.panel, styles.promptPanel]}>
          <Text style={styles.promptTitle}>Weather couldn&apos;t load</Text>
          <Text style={styles.promptSub}>{fetchError}</Text>
        </View>
      </View>
    );
  }

  if (loading && !data) {
    return (
      <View style={styles.wrap}>
        <View style={styles.panel}>
          <ActivityIndicator color={Palette.forest} />
        </View>
      </View>
    );
  }

  if (!data) return null;

  const Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }> =
    iconForCondition(data.iconKey, data.shortForecast);
  const frosty = data.next48LowF != null && data.next48LowF <= 35;

  if (compact) {
    // Rail layout: one current-conditions row + up to three stacked
    // forecast rows. No stat grid — the rail is a glance, the home
    // tab has the full spread.
    return (
      <View style={[styles.compactCard, frosty && styles.panelFrost]}>
        <View style={styles.compactNow}>
          <View style={[styles.iconBox, frosty && styles.iconBoxFrost]}>
            <Icon size={22} color={frosty ? '#3D5670' : Palette.forest} strokeWidth={1.7} />
          </View>
          <Text style={styles.compactTemp}>{Math.round(data.tempF)}°</Text>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={styles.compactCond} numberOfLines={1}>{data.shortForecast}</Text>
            <Text style={styles.compactMeta} numberOfLines={1}>
              {data.feelsLikeF != null ? `Feels ${Math.round(data.feelsLikeF)}°` : ''}
              {data.humidityPct != null ? ` · ${data.humidityPct}%` : ''}
              {data.windSpeed ? ` · ${data.windSpeed}` : ''}
            </Text>
          </View>
        </View>
        {frosty ? (
          <Text style={styles.compactFrost}>Frost watch — {Math.round(data.next48LowF!)}° in the next 48h</Text>
        ) : null}
        {data.upcoming.slice(0, 3).map((d, i) => {
          const DayIcon = iconForCondition(d.iconKey, d.shortForecast);
          return (
            <View
              key={d.name}
              style={[styles.compactDay, i === 0 && styles.compactDayFirst]}>
              <Text style={styles.compactDayName}>{d.name}</Text>
              <DayIcon size={15} color={Palette.inkSoft} strokeWidth={1.8} />
              <Text style={styles.compactDayTemp}>
                {d.highF != null ? `${Math.round(d.highF)}°` : '—'}
                <Text style={styles.compactDayLow}>
                  {d.lowF != null ? ` / ${Math.round(d.lowF)}°` : ''}
                </Text>
              </Text>
            </View>
          );
        })}
      </View>
    );
  }

  return (
    <View style={styles.wrap}>
      <View style={[styles.panel, frosty && styles.panelFrost]}>
        {/* Current conditions (left side) */}
        <View style={styles.nowBlock}>
          <View style={[styles.iconBox, frosty && styles.iconBoxFrost]}>
            <Icon
              size={24}
              color={frosty ? '#3D5670' : Palette.forest}
              strokeWidth={1.7}
            />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={styles.temp}>
              {Math.round(data.tempF)}°
              <Text style={styles.tempUnit}> F</Text>
            </Text>
            <Text style={styles.forecast} numberOfLines={1}>
              {data.shortForecast}
            </Text>
            {frosty ? (
              <View style={styles.frostBadgeInline}>
                <Snowflake size={10} color={'#FBF6EA'} strokeWidth={2.2} />
                <Text style={styles.frostBadgeText}>FROST WATCH · 48h</Text>
              </View>
            ) : data.windSpeed ? (
              <View style={styles.windInline}>
                <Wind size={10} color={Palette.inkSoft} strokeWidth={2} />
                <Text style={styles.windText}>{data.windSpeed}</Text>
              </View>
            ) : null}
          </View>
        </View>

        {/* Middle extras column — only visible on wider viewports
            (>=700) where there's room between the current temp and
            the day-forecast cards. Surfaces fields people care about
            mid-day: humidity, UV peak, sunrise/sunset. */}
        {viewportWidth >= 700 ? (
          <View style={styles.extrasBlock}>
            {data.humidityPct != null ? (
              <ExtraRow Icon={Droplets} label="Humidity" value={`${Math.round(data.humidityPct)}%`} />
            ) : null}
            {data.uvIndexToday != null ? (
              <ExtraRow Icon={Sun} label="UV peak" value={String(Math.round(data.uvIndexToday))} />
            ) : null}
            {data.sunrise ? (
              <ExtraRow Icon={SunriseIcon} label="Sunrise" value={formatTime(data.sunrise)} />
            ) : null}
            {data.sunset ? (
              <ExtraRow Icon={SunsetIcon} label="Sunset" value={formatTime(data.sunset)} />
            ) : null}
          </View>
        ) : null}

        {/* Upcoming days — count adapts to viewport (2 mobile / 5 tablet / 7 desktop). */}
        {data.upcoming.length > 0 ? (
          <View style={styles.upcomingBlock}>
            {data.upcoming.slice(0, forecastDays).map((d, i) => {
              const DayIcon = iconForCondition(d.iconKey, d.shortForecast);
              return (
                <View key={`${d.name}-${i}`} style={styles.dayCell}>
                  <Text style={styles.dayName}>{d.name}</Text>
                  <DayIcon size={18} color={Palette.forest} strokeWidth={1.7} />
                  <View style={styles.dayTemps}>
                    {d.highF != null ? (
                      <Text style={styles.dayHigh}>{Math.round(d.highF)}°</Text>
                    ) : null}
                    {d.lowF != null ? (
                      <Text style={styles.dayLow}>{Math.round(d.lowF)}°</Text>
                    ) : null}
                  </View>
                </View>
              );
            })}
          </View>
        ) : null}
      </View>
    </View>
  );
}

function ExtraRow({
  Icon,
  label,
  value,
}: {
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  label: string;
  value: string;
}) {
  return (
    <View style={styles.extraRow}>
      <Icon size={11} color={Palette.inkSoft} strokeWidth={1.8} />
      <Text style={styles.extraLabel} numberOfLines={1}>{label}</Text>
      <Text style={styles.extraValue} numberOfLines={1}>{value}</Text>
    </View>
  );
}

/**
 * Open-Meteo returns sunrise/sunset as full ISO strings in local time
 * (because we passed timezone=auto). Convert to 12-hour AM/PM since US
 * users overwhelmingly expect that format — "8:55 PM" reads instantly,
 * "20:55" needs a mental translation.
 */
function formatTime(iso: string): string {
  if (iso.length < 16) return iso;
  const hh = parseInt(iso.slice(11, 13), 10);
  const mm = iso.slice(14, 16);
  if (Number.isNaN(hh)) return iso.slice(11, 16);
  const period = hh >= 12 ? 'PM' : 'AM';
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${mm} ${period}`;
}

function iconForCondition(iconKey: string, shortForecast: string): ComponentType<{
  size?: number;
  color?: string;
  strokeWidth?: number;
}> {
  const t = (iconKey + ' ' + shortForecast).toLowerCase();
  if (/snow|flurr|sleet|blizzard/.test(t)) return CloudSnow;
  if (/rain|shower|drizzle|thunder|storm/.test(t)) return CloudRain;
  if (/cloud|overcast|fog|haz/.test(t)) return CloudSun;
  if (/sun|clear|fair/.test(t)) return Sun;
  return Thermometer;
}

async function fetchWeather({
  lat: explicitLat,
  lon: explicitLon,
  zip,
}: {
  lat?: number | null;
  lon?: number | null;
  zip: string | null;
}): Promise<WeatherSnapshot> {
  // Geocode if we don't already have coordinates. Open-Meteo's geocoder is
  // RN-Android friendly (no UA requirement, plain JSON).
  let lat: number | null = explicitLat ?? null;
  let lon: number | null = explicitLon ?? null;
  if ((lat == null || lon == null) && zip && /^\d{5}$/.test(zip)) {
    // zippopotam.us has a clean ZIP→coordinates endpoint, no API key,
    // CORS-friendly, and unlike Open-Meteo's geocoder it accepts ZIP
    // directly (Open-Meteo only supports `name=` city/place searches —
    // sending `postal_code=` to it returns HTTP 400).
    const geoUrl = `https://api.zippopotam.us/us/${zip}`;
    const geoRes = await fetch(geoUrl);
    if (!geoRes.ok) throw new Error(`ZIP lookup HTTP ${geoRes.status}`);
    const geoData: {
      places?: Array<{ latitude: string; longitude: string }>;
    } = await geoRes.json();
    const hit = geoData.places?.[0];
    if (!hit) throw new Error(`No location for ZIP ${zip}`);
    // zippopotam returns numeric strings — parse to numbers.
    lat = parseFloat(hit.latitude);
    lon = parseFloat(hit.longitude);
    if (Number.isNaN(lat) || Number.isNaN(lon)) {
      throw new Error(`ZIP ${zip} returned invalid coordinates`);
    }
  }
  if (lat == null || lon == null) throw new Error('No coordinates and no ZIP');

  // One Open-Meteo call gives us current, hourly (48h frost window), and
  // daily (next 2 days). All in Fahrenheit, all in the user's local TZ.
  // Pull a full 8-day forecast (today + 7 ahead) plus extras that
  // surface as a secondary strip on the home screen: humidity, UV peak,
  // sunrise/sunset. The renderer hides/shows fields based on viewport.
  const wxUrl =
    `https://api.open-meteo.com/v1/forecast` +
    `?latitude=${lat.toFixed(4)}&longitude=${lon.toFixed(4)}` +
    `&current=temperature_2m,weather_code,wind_speed_10m,relative_humidity_2m,apparent_temperature` +
    `&hourly=temperature_2m,weather_code` +
    `&daily=temperature_2m_max,temperature_2m_min,weather_code,sunrise,sunset,uv_index_max` +
    `&temperature_unit=fahrenheit&wind_speed_unit=mph` +
    `&forecast_days=8&timezone=auto`;
  // No Accept header — keep this a CORS-simple GET (no preflight).
  const wxRes = await fetch(wxUrl);
  if (!wxRes.ok) throw new Error(`Open-Meteo HTTP ${wxRes.status}`);
  const wxData: {
    current?: {
      temperature_2m: number;
      weather_code: number;
      wind_speed_10m: number;
      relative_humidity_2m?: number;
      apparent_temperature?: number;
    };
    hourly?: { time: string[]; temperature_2m: number[]; weather_code: number[] };
    daily?: {
      time: string[];
      temperature_2m_max: number[];
      temperature_2m_min: number[];
      weather_code: number[];
      sunrise?: string[];
      sunset?: string[];
      uv_index_max?: number[];
    };
  } = await wxRes.json();

  if (!wxData.current) throw new Error('No current weather');

  // 48h frost window: lowest hourly temp across the next 48 hours.
  let next48LowF: number | null = null;
  const hourly = wxData.hourly;
  if (hourly && hourly.temperature_2m?.length) {
    const slice = hourly.temperature_2m.slice(0, 48);
    next48LowF = Math.min(...slice);
  }

  // Next 2 days from daily (skip index 0 which is today — current panel
  // already shows today).
  // Pull up to 7 future days from the daily forecast. The renderer
  // decides how many to actually show based on viewport width.
  const upcoming: DayForecast[] = [];
  const daily = wxData.daily;
  if (daily && daily.time?.length) {
    // Skip index 0 (today — already shown in the "current" tile) and
    // collect the next 7 days. The renderer slices by viewport.
    for (let i = 1; i < Math.min(daily.time.length, 8); i++) {
      const isoDate = daily.time[i];
      // ISO 'YYYY-MM-DD' parsed as local-midnight to avoid TZ shifting day name.
      const [y, m, d] = isoDate.split('-').map((n) => parseInt(n, 10));
      const dayDate = new Date(y, (m ?? 1) - 1, d ?? 1);
      const code = daily.weather_code[i];
      upcoming.push({
        name: shortDayName(dayDate),
        highF: daily.temperature_2m_max[i] ?? null,
        lowF: daily.temperature_2m_min[i] ?? null,
        shortForecast: describeWmoCode(code),
        iconKey: wmoToIconKey(code),
      });
    }
  }

  return {
    tempF: wxData.current.temperature_2m,
    feelsLikeF: wxData.current.apparent_temperature ?? null,
    shortForecast: describeWmoCode(wxData.current.weather_code),
    iconKey: wmoToIconKey(wxData.current.weather_code),
    windSpeed:
      wxData.current.wind_speed_10m != null
        ? `${Math.round(wxData.current.wind_speed_10m)} mph wind`
        : null,
    humidityPct: wxData.current.relative_humidity_2m ?? null,
    uvIndexToday: wxData.daily?.uv_index_max?.[0] ?? null,
    sunrise: wxData.daily?.sunrise?.[0] ?? null,
    sunset: wxData.daily?.sunset?.[0] ?? null,
    next48LowF,
    upcoming,
    cachedAt: Date.now(),
  };
}

// WMO weather code → human label. Reference: https://open-meteo.com/en/docs
// We collapse the long table into broad buckets that match the icon picker.
function describeWmoCode(code: number | undefined): string {
  if (code == null) return '';
  if (code === 0) return 'Clear';
  if (code === 1) return 'Mostly clear';
  if (code === 2) return 'Partly cloudy';
  if (code === 3) return 'Overcast';
  if (code >= 45 && code <= 48) return 'Fog';
  if (code >= 51 && code <= 57) return 'Drizzle';
  if (code >= 61 && code <= 67) return 'Rain';
  if (code >= 71 && code <= 77) return 'Snow';
  if (code >= 80 && code <= 82) return 'Rain showers';
  if (code === 85 || code === 86) return 'Snow showers';
  if (code >= 95) return 'Thunderstorm';
  return 'Mixed';
}

// Map WMO → token the existing iconForCondition matcher understands.
function wmoToIconKey(code: number | undefined): string {
  if (code == null) return '';
  if (code === 0 || code === 1) return 'clear';
  if (code === 2 || code === 3) return 'cloud';
  if (code >= 45 && code <= 48) return 'fog';
  if (code >= 51 && code <= 67) return 'rain';
  if (code >= 71 && code <= 77) return 'snow';
  if (code >= 80 && code <= 82) return 'shower';
  if (code === 85 || code === 86) return 'snow';
  if (code >= 95) return 'thunder';
  return 'cloud';
}

function shortDayName(d: Date): string {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return d.toLocaleDateString(undefined, { weekday: 'short' });
}

const styles = StyleSheet.create({
  // Compact rail layout (Wave 71).
  compactCard: {
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: 16,
    padding: 12,
  },
  compactNow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  compactTemp: {
    fontFamily: Fonts.serifBold, fontSize: 26, color: Palette.ink,
    letterSpacing: -0.5,
  },
  compactCond: { fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.ink },
  compactMeta: {
    marginTop: 1,
    fontFamily: Fonts.sansMedium, fontSize: 11.5, color: Palette.inkSoft,
  },
  compactFrost: {
    marginTop: 8,
    fontFamily: Fonts.sansBold, fontSize: 11.5, color: '#3D5670',
  },
  compactDay: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingVertical: 7,
    borderTopWidth: 1, borderTopColor: Palette.line,
  },
  compactDayFirst: { marginTop: 10 },
  compactDayName: {
    width: 76,
    fontFamily: Fonts.sansBold, fontSize: 11.5, color: Palette.inkSoft,
    textTransform: 'uppercase', letterSpacing: 0.4,
  },
  compactDayTemp: {
    flex: 1, textAlign: 'right',
    fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.ink,
  },
  compactDayLow: { fontFamily: Fonts.sansMedium, color: Palette.inkSoft, fontSize: 12 },

  wrap: { paddingHorizontal: Spacing.four, marginTop: Spacing.three },
  promptPanel: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
  },
  promptTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.ink,
  },
  promptSub: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    lineHeight: 17,
  },
  panel: {
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.xl,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  panelFrost: {
    backgroundColor: '#DDE6F2',
    borderColor: '#A8BED9',
  },
  nowBlock: { flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 },
  extrasBlock: {
    flex: 0,
    flexBasis: 'auto',
    minWidth: 110,
    paddingHorizontal: 12,
    justifyContent: 'center',
    gap: 6,
  },
  extraRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  extraLabel: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 11.5,
    color: Palette.inkSoft,
    letterSpacing: 0.2,
  },
  extraValue: {
    marginLeft: 'auto',
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.ink,
  },
  upcomingBlock: {
    flexDirection: 'row',
    gap: 6,
    marginLeft: 8,
  },
  dayCell: {
    backgroundColor: 'rgba(220,231,225,0.55)',
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 6,
    alignItems: 'center',
    gap: 3,
    minWidth: 56,
  },
  dayName: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.forest,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  dayTemps: { flexDirection: 'row', alignItems: 'baseline', gap: 4 },
  dayHigh: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.ink,
  },
  dayLow: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 12,
    color: Palette.inkFaint,
  },
  iconBox: {
    width: 44, height: 44, borderRadius: 13,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  iconBoxFrost: { backgroundColor: '#C6D6EA' },
  temp: {
    fontFamily: Fonts.serifBold,
    fontSize: 22,
    color: Palette.ink,
    letterSpacing: -0.5,
    lineHeight: 26,
  },
  tempUnit: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.inkSoft,
  },
  forecast: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 13.5,
    color: Palette.inkSoft,
    marginTop: 1,
  },
  frostBadgeInline: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#3D5670',
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 999,
    marginTop: 4,
  },
  frostBadgeText: {
    fontFamily: Fonts.sansBold,
    fontSize: 10.5,
    color: Palette.cream,
    letterSpacing: 0.5,
  },
  windInline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 3,
  },
  windText: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 11.5,
    color: Palette.inkSoft,
  },
});
